import { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import {
  ClinicProgram,
  AvailableProduct,
  clinicProgramService,
} from '@/services/clinicProgramService'

interface ClinicAssignProductsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  clinicId: string
  program: ClinicProgram
  onSuccess: () => void
}

const ClinicAssignProductsDialog = ({
  open,
  onOpenChange,
  clinicId,
  program,
  onSuccess,
}: ClinicAssignProductsDialogProps) => {
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [products, setProducts] = useState<AvailableProduct[]>([])
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([])

  useEffect(() => {
    if (!open) return

    const productIds = (program.program_products || []).map(pp => pp.product_id)
    setSelectedProductIds(productIds)
    setSearch('')

    const fetchProducts = async () => {
      try {
        setIsLoading(true)
        const data = await clinicProgramService.getAvailableProducts(clinicId)
        setProducts(data || [])
      } catch (error) {
        console.error(error)
        toast({
          title: 'Error',
          description: 'Failed to fetch available products',
          variant: 'destructive',
        })
      } finally {
        setIsLoading(false)
      }
    }

    fetchProducts()
  }, [open, clinicId, program, toast])

  const filteredProducts = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return products
    return products.filter(product => product.description.toLowerCase().includes(query))
  }, [products, search])

  const handleToggle = (productId: string, checked: boolean) => {
    setSelectedProductIds(prev => {
      if (checked) {
        if (prev.includes(productId)) return prev
        return [...prev, productId]
      }
      return prev.filter(id => id !== productId)
    })
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)
      await clinicProgramService.assignProducts(clinicId, program.id, selectedProductIds)
      toast({
        title: 'Success',
        description: 'Program products updated successfully',
        variant: 'success',
      })
      onSuccess()
      onOpenChange(false)
    } catch (error) {
      console.error(error)
      toast({
        title: 'Error',
        description: 'Failed to update products',
        variant: 'destructive',
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Assign Products</DialogTitle>
          <DialogDescription>
            Select products for "{program.title}".
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="product-search">Search products</Label>
            <Input
              id="product-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by product description"
            />
          </div>

          <div className="max-h-80 overflow-y-auto border rounded-md p-2 space-y-1">
            {isLoading && (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
              </div>
            )}

            {!isLoading && filteredProducts.length === 0 && (
              <p className="text-sm text-slate-500 py-6 text-center">
                No products found
              </p>
            )}

            {!isLoading &&
              filteredProducts.map(product => {
                const checked = selectedProductIds.includes(product.id)
                return (
                  <label
                    key={product.id}
                    className="flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer hover:bg-slate-50"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={value => handleToggle(product.id, Boolean(value))}
                    />
                    <span className="text-sm text-slate-700">{product.description}</span>
                  </label>
                )
              })}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || isLoading}>
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ClinicAssignProductsDialog
